/**
 * Battery Calculator Hook
 * Custom hook for battery capacity calculations with validation
 */

import { useMemo } from 'react';

export interface BatteryCalculatorInput {
  systemVoltage: number;
  standbyCurrent: number;
  alarmCurrent: number;
  standbyTime: number;
  alarmTime: number;
  temperature: number;
  batteryType: 'sealed_lead_acid' | 'lithium';
}

export interface BatteryCalculatorResult {
  requiredCapacity: number;
  recommendedCapacity: number;
  standbyLoad: number;
  alarmLoad: number;
  totalLoad: number;
  temperatureFactor: number;
  agingFactor: number;
  safetyMargin: number;
  nfpaCompliant: boolean;
  recommendations: string[];
}

export interface BatteryCalculatorErrors {
  systemVoltage?: string;
  standbyCurrent?: string;
  alarmCurrent?: string;
  standbyTime?: string;
  alarmTime?: string;
  temperature?: string;
  batteryType?: string;
}

/**
 * Get temperature derating factor per NFPA 72
 */
function getTemperatureFactor(tempF: number, batteryType: string): number {
  if (batteryType === 'lithium') {
    if (tempF >= 32 && tempF <= 104) return 1.0;
    if (tempF < 32) return 0.9;
    return 0.95;
  }
  
  // Sealed lead-acid
  if (tempF >= 77) return 1.0;
  if (tempF >= 60) return 0.9;
  if (tempF >= 40) return 0.8;
  if (tempF >= 20) return 0.7;
  return 0.6;
}

/**
 * Get aging factor per NFPA 72
 */
function getAgingFactor(batteryType: string): number {
  return batteryType === 'lithium' ? 1.15 : 1.25;
}

/**
 * Validate input values
 */
function validateInputs(input: BatteryCalculatorInput): BatteryCalculatorErrors {
  const errors: BatteryCalculatorErrors = {};

  if (![12, 24].includes(input.systemVoltage)) {
    errors.systemVoltage = 'System voltage must be 12V or 24V';
  }

  if (input.standbyCurrent < 0) {
    errors.standbyCurrent = 'Standby current must be non-negative';
  }

  if (input.alarmCurrent < 0) {
    errors.alarmCurrent = 'Alarm current must be non-negative';
  }

  if (input.standbyTime < 24) {
    errors.standbyTime = 'NFPA 72 requires minimum 24 hours standby time';
  }

  if (input.alarmTime < 5) {
    errors.alarmTime = 'NFPA 72 requires minimum 5 minutes alarm time';
  }

  if (input.temperature < -40 || input.temperature > 140) {
    errors.temperature = 'Temperature must be between -40°F and 140°F';
  }

  if (!['sealed_lead_acid', 'lithium'].includes(input.batteryType)) {
    errors.batteryType = 'Invalid battery type';
  }

  return errors;
}

/**
 * Calculate battery capacity
 */
function calculateCapacity(input: BatteryCalculatorInput): BatteryCalculatorResult {
  const { standbyCurrent, alarmCurrent, standbyTime, alarmTime, temperature, batteryType } = input;

  // Convert alarm time from minutes to hours
  const alarmTimeHours = alarmTime / 60;

  // Calculate loads
  const standbyLoad = standbyCurrent * standbyTime;
  const alarmLoad = alarmCurrent * alarmTimeHours;
  const totalLoad = standbyLoad + alarmLoad;

  // Apply derating factors
  const temperatureFactor = getTemperatureFactor(temperature, batteryType);
  const agingFactor = getAgingFactor(batteryType);
  const safetyMargin = 1.2;

  // Calculate required capacity
  const requiredCapacity = (totalLoad * agingFactor) / temperatureFactor;
  const recommendedCapacity = requiredCapacity * safetyMargin;

  // Generate recommendations
  const recommendations: string[] = [];

  if (temperature < 60) {
    recommendations.push('Consider battery heating or insulation due to low temperature');
  }

  if (temperature > 100) {
    recommendations.push('Consider battery cooling or ventilation due to high temperature');
  }

  if (standbyTime > 24) {
    recommendations.push('Extended standby time requires larger battery capacity');
  }

  if (alarmTime > 5) {
    recommendations.push('Extended alarm time may require additional battery capacity');
  }

  if (batteryType === 'sealed_lead_acid' && requiredCapacity > 100) {
    recommendations.push('Consider lithium batteries for large capacity requirements');
  }

  // Standard battery sizes
  const standardSizes = [7, 12, 18, 26, 35, 55, 75, 100, 150, 200];
  const nextStandardSize = standardSizes.find(size => size >= recommendedCapacity);

  if (nextStandardSize) {
    recommendations.push(`Recommended standard battery size: ${nextStandardSize}Ah`);
  }

  const nfpaCompliant = requiredCapacity > 0 && temperatureFactor > 0;

  if (!nfpaCompliant) {
    recommendations.push('WARNING: Configuration does not meet NFPA 72 requirements');
  }

  return {
    requiredCapacity: Math.round(requiredCapacity * 100) / 100,
    recommendedCapacity: Math.round(recommendedCapacity * 100) / 100,
    standbyLoad: Math.round(standbyLoad * 100) / 100,
    alarmLoad: Math.round(alarmLoad * 100) / 100,
    totalLoad: Math.round(totalLoad * 100) / 100,
    temperatureFactor,
    agingFactor,
    safetyMargin,
    nfpaCompliant,
    recommendations,
  };
}

/**
 * Battery Calculator Hook
 */
export function useBatteryCalculator(input: BatteryCalculatorInput) {
  // Validate inputs
  const errors = useMemo(() => validateInputs(input), [
    input.systemVoltage,
    input.standbyCurrent,
    input.alarmCurrent,
    input.standbyTime,
    input.alarmTime,
    input.temperature,
    input.batteryType,
  ]);

  const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);

  // Calculate result
  const result = useMemo(() => {
    if (!isValid) return null;
    
    try {
      return calculateCapacity(input);
    } catch (error) {
      console.error('Battery calculation error:', error);
      return null;
    }
  }, [
    isValid,
    input.systemVoltage,
    input.standbyCurrent,
    input.alarmCurrent,
    input.standbyTime,
    input.alarmTime,
    input.temperature,
    input.batteryType,
  ]);

  return {
    result,
    errors,
    isValid,
  };
}
